import { translate } from '../helpers/translate.helper';

interface PasswordCriteria {
    hasSpecialChar: boolean;
    hasLowerCase: boolean;
    hasUpperCase: boolean;
    hasNumber: boolean;
    isNotEmpty: boolean;
    hasMinLength: boolean;
    mainCriteriaCount: number;
    mainCriteria: boolean[];
}

const mapNumberToText = (number: number) => {
    switch (number) {
        case 1:
            return 'one';
        case 2:
            return 'two';
        default:
            return '';
    }
};

interface PasswordFeedback {
    warning: string;
    suggestions: string[];
}

interface PasswordStrengthResult {
    score: number;
    feedback: PasswordFeedback;
}

const getPasswordWarning = (criteria: PasswordCriteria): string => {
    const { isNotEmpty, hasMinLength, mainCriteriaCount } = criteria;

    if (!isNotEmpty) {
        return translate('passwordStrength.error.empty');
    }

    if (!hasMinLength) {
        return translate('passwordStrength.error.minLength');
    }

    if (mainCriteriaCount < 3) {
        return translate('passwordStrength.error.needsMoreTypes', {
            missingTypes: mapNumberToText(3 - mainCriteriaCount),
        });
    }

    return '';
};

const getPasswordSuggestions = (criteria: PasswordCriteria): string[] => {
    const { isNotEmpty, hasMinLength, hasLowerCase, hasUpperCase, hasNumber, hasSpecialChar } = criteria;

    if (!isNotEmpty) {
        return [];
    }

    const possibleSuggestions = [
        { condition: !hasMinLength, suggestion: translate('passwordStrength.suggestion.minLength') },
        { condition: !hasLowerCase, suggestion: translate('passwordStrength.suggestion.lowercase') },
        { condition: !hasUpperCase, suggestion: translate('passwordStrength.suggestion.uppercase') },
        { condition: !hasNumber, suggestion: translate('passwordStrength.suggestion.number') },
        { condition: !hasSpecialChar, suggestion: translate('passwordStrength.suggestion.specialChar') },
    ];

    return possibleSuggestions.filter(({ condition }) => condition).map(({ suggestion }) => suggestion);
};

const getPasswordScore = (criteria: PasswordCriteria): number => {
    const { isNotEmpty, hasMinLength, mainCriteriaCount } = criteria;

    if (!isNotEmpty) {
        return 0;
    }

    if (!hasMinLength || mainCriteriaCount === 0) {
        return 1;
    }

    if (mainCriteriaCount <= 2) {
        return 2;
    }

    if (mainCriteriaCount === 3) {
        return 3;
    }

    return 4;
};

export const usePasswordStrength = (password: string): PasswordStrengthResult => {
    const hasSpecialChar = /[!@#$%^&*]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasUpperCase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const isNotEmpty = password.length > 0;
    const hasMinLength = password.length >= 8;

    const mainCriteria = [hasSpecialChar, hasLowerCase, hasUpperCase, hasNumber];
    const mainCriteriaCount = mainCriteria.filter(Boolean).length;

    const criteria: PasswordCriteria = {
        hasSpecialChar,
        hasLowerCase,
        hasUpperCase,
        hasNumber,
        isNotEmpty,
        hasMinLength,
        mainCriteria,
        mainCriteriaCount,
    };

    return {
        score: getPasswordScore(criteria),
        feedback: {
            warning: getPasswordWarning(criteria),
            suggestions: getPasswordSuggestions(criteria),
        },
    };
};
